import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { FileText, GitCompare, Play, RotateCcw, CheckCircle, LoaderCircle, ScrollText, PlayCircle } from 'lucide-react'
import { toastr } from 'react-redux-toastr'
import { Query, Mutation } from '../../../../api'
import { useModuleActions } from '../../../../hooks/useMyPermissions'

import AiTenderTab from './AiTenderTab'
import AiExtractedItemsTable, { type RowCorrection } from './AiExtractedItemsTable'
import AiProcessingProgress from './AiProcessingProgress'
import AiBidComparison from './AiBidComparison'
import AiProcessingLogModal from './AiProcessingLogModal'
import ExportEvaluationXLSX from '../ExportEvaluationXLSX'

interface Props {
  tender: any
  round: number
  projectId?: string
}

const AiEvaluation: React.FC<Props> = ({ tender, round, projectId }) => {
  const { t } = useTranslation()
  const { canCreate, canExport } = useModuleActions('AI_EVALUATION', projectId)
  const [activeTab, setActiveTab] = useState('tender')
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [activeJobContext, setActiveJobContext] = useState<string>('tender')
  const [refreshKey, setRefreshKey] = useState(0)
  const [logJobId, setLogJobId] = useState<string | null>(null)
  const [batchQueue, setBatchQueue] = useState<string[]>([])
  const batchQueueRef = useRef<string[]>([])
  const prevProcessingRef = useRef<Set<string>>(new Set())

  const hasActiveJobs = (jobsList: any[]) =>
    jobsList.some((j: any) => j.status === 'PROCESSING' || j.status === 'PENDING')

  const { data, refetch } = useQuery(Query.AI_PROCESSING_JOBS, {
    variables: { tenderId: tender.id },
    skip: !tender.id,
    fetchPolicy: 'cache-and-network',
  })

  const jobs = data?.aiProcessingJobs || []

  // Poll JOBS query while any job is active or batch queue is non-empty
  useEffect(() => {
    const shouldPoll = hasActiveJobs(jobs) || batchQueueRef.current.length > 0 || !!activeJobId
    if (!shouldPoll) return

    const interval = setInterval(() => refetch(), 3000)
    return () => clearInterval(interval)
  }, [hasActiveJobs(jobs), batchQueue.length, activeJobId, refetch])

  // Restore active job from server data on mount/reload
  useEffect(() => {
    if (!activeJobId && jobs.length > 0) {
      const runningJob = jobs.find(
        (j: any) => j.status === 'PROCESSING' || j.status === 'PENDING',
      )
      if (runningJob) {
        const context =
          runningJob.type === 'COMPARISON'
            ? 'comparison'
            : runningJob.type === 'BID_EXTRACTION' && runningJob.bidId
              ? `bid-${runningJob.bidId}`
              : 'tender'
        setActiveJobId(runningJob.id)
        setActiveJobContext(context)
        setActiveTab(context)
      }
    }
  }, [jobs, activeJobId])

  const [startBidExtraction, { loading: extractingBid }] = useMutation(
    Mutation.START_AI_BID_EXTRACTION,
  )
  const [startComparison, { loading: comparing }] = useMutation(
    Mutation.START_AI_COMPARISON,
  )
  const [submitCorrections, { loading: submittingCorrections }] = useMutation(
    Mutation.SUBMIT_EXTRACTION_CORRECTIONS,
  )

  // Bids in the current round with uploaded files
  const bids = useMemo(
    () =>
      (tender.bids || []).filter(
        (b: any) => b.round === round && b.files && b.files.length > 0,
      ),
    [tender.bids, round],
  )

  const isBidProcessed = (bidId: string) =>
    jobs.some(
      (j: any) =>
        j.type === 'BID_EXTRACTION' && j.status === 'COMPLETED' && j.bidId === bidId,
    )

  const isBidProcessing = (bidId: string) =>
    (!!activeJobId && activeJobContext === `bid-${bidId}`) ||
    jobs.some(
      (j: any) =>
        j.type === 'BID_EXTRACTION' &&
        (j.status === 'PROCESSING' || j.status === 'PENDING') &&
        j.bidId === bidId,
    ) || batchQueueRef.current.includes(bidId)

  const getBidJobId = (bidId: string) =>
    jobs.find(
      (j: any) =>
        j.type === 'BID_EXTRACTION' && j.status === 'COMPLETED' && j.bidId === bidId,
    )?.id

  const getActiveBidJobId = (bidId: string) =>
    jobs.find(
      (j: any) =>
        j.type === 'BID_EXTRACTION' &&
        (j.status === 'PROCESSING' || j.status === 'PENDING') &&
        j.bidId === bidId,
    )?.id

  const allBidsProcessed =
    bids.length > 0 &&
    bids.every((b: any) => isBidProcessed(b.id))

  const comparisonJob = jobs.find(
    (j: any) => j.type === 'COMPARISON' && j.status === 'COMPLETED',
  )

  const handleJobStarted = useCallback((jobId: string, context: string = 'tender') => {
    setActiveJobId(jobId)
    setActiveJobContext(context)
  }, [])

  const handleJobComplete = useCallback(async () => {
    await refetch()
    setActiveJobId(null)
    setRefreshKey(k => k + 1)
  }, [refetch])

  // Detect bid completion from polled jobs and progress batch queue
  useEffect(() => {
    const currentlyProcessing = new Set<string>()
    jobs.forEach((j: any) => {
      if (j.type === 'BID_EXTRACTION' && (j.status === 'PROCESSING' || j.status === 'PENDING')) {
        currentlyProcessing.add(j.bidId)
      }
    })

    // Detect transitions: was processing → no longer processing
    const justCompleted: string[] = []
    prevProcessingRef.current.forEach((bidId) => {
      if (!currentlyProcessing.has(bidId)) {
        justCompleted.push(bidId)
      }
    })
    prevProcessingRef.current = currentlyProcessing

    if (justCompleted.length === 0) return

    // A bid just finished — refresh data
    setRefreshKey(k => k + 1)

    // If there's a batch queue, start the next bid
    if (batchQueueRef.current.length > 0) {
      const nextBidId = batchQueueRef.current[0]
      batchQueueRef.current = batchQueueRef.current.slice(1)
      setBatchQueue([...batchQueueRef.current])

      startBidExtraction({
        variables: { tenderId: tender.id, bidId: nextBidId },
      }).then(({ data }) => {
        if (data?.startAiBidExtraction?.id) {
          handleJobStarted(data.startAiBidExtraction.id, `bid-${nextBidId}`)
          setActiveTab(`bid-${nextBidId}`)
        }
      }).catch((err: any) => {
        toastr.error(t('Common_error'), err.message)
      })
    }
  }, [jobs])

  const handleStartBidExtraction = async (bidId: string) => {
    try {
      const { data } = await startBidExtraction({
        variables: { tenderId: tender.id, bidId },
      })
      if (data?.startAiBidExtraction?.id) {
        handleJobStarted(data.startAiBidExtraction.id, `bid-${bidId}`)
        refetch()
      }
    } catch (err: any) {
      toastr.error(t('Common_error'), err.message)
    }
  }

  const handleProcessAllBids = async () => {
    // Process unprocessed bids, or reprocess all if every bid is already done
    const unprocessedBids = bids.filter((b: any) => !isBidProcessed(b.id) && !isBidProcessing(b.id))
    const bidsToProcess = unprocessedBids.length > 0
      ? unprocessedBids
      : bids.filter((b: any) => !isBidProcessing(b.id))
    if (bidsToProcess.length === 0) return

    // Start first bid immediately, queue the rest
    const [first, ...rest] = bidsToProcess
    batchQueueRef.current = rest.map((b: any) => b.id)
    setBatchQueue([...batchQueueRef.current])

    try {
      const { data } = await startBidExtraction({
        variables: { tenderId: tender.id, bidId: first.id },
      })
      if (data?.startAiBidExtraction?.id) {
        handleJobStarted(data.startAiBidExtraction.id, `bid-${first.id}`)
        setActiveTab(`bid-${first.id}`)
        refetch()
      }
    } catch (err: any) {
      toastr.error(t('Common_error'), err.message)
      batchQueueRef.current = []
      setBatchQueue([])
    }
  }

  const handleStartComparison = async () => {
    try {
      const { data } = await startComparison({
        variables: { tenderId: tender.id },
      })
      if (data?.startAiComparison?.id) {
        handleJobStarted(data.startAiComparison.id, 'comparison')
      }
    } catch (err: any) {
      toastr.error(t('Common_error'), err.message)
    }
  }

  const handleSubmitCorrections = useCallback(async (corrections: RowCorrection[], bidId?: string) => {
    try {
      const { data } = await submitCorrections({
        variables: {
          tenderId: tender.id,
          bidId: bidId || null,
          corrections,
        },
      })
      if (data?.submitExtractionCorrections?.id) {
        const context = bidId ? `bid-${bidId}` : 'tender'
        handleJobStarted(data.submitExtractionCorrections.id, context)
        toastr.success(t('Common_success'), t('ai_corrections_submitted', 'Re-extraction started with corrections'))
        refetch()
      }
    } catch (err: any) {
      toastr.error(t('Common_error'), err.message)
    }
  }, [submitCorrections, tender.id, handleJobStarted, refetch, t])

  const unprocessedCount = bids.filter((b: any) => !isBidProcessed(b.id) && !isBidProcessing(b.id)).length
  const isBatchRunning = batchQueueRef.current.length > 0 || hasActiveJobs(jobs)

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <div className="flex items-center gap-2 flex-wrap">
        <TabsList>
          <TabsTrigger value="tender" className="gap-1.5">
            <FileText className="h-4 w-4" />
            {t('ai_tab_tender')}
          </TabsTrigger>
          {bids.map((bid: any) => (
            <TabsTrigger key={bid.id} value={`bid-${bid.id}`} className="gap-1.5">
              {bid.bidder?.name || bid.id}
              {isBidProcessing(bid.id) ? (
                <LoaderCircle className="h-3 w-3 animate-spin text-blue-600" />
              ) : isBidProcessed(bid.id) ? (
                <CheckCircle className="h-3 w-3 text-green-600" />
              ) : null}
            </TabsTrigger>
          ))}
          {allBidsProcessed && (
            <TabsTrigger value="comparison" className="gap-1.5">
              <GitCompare className="h-4 w-4" />
              {t('ai_tab_comparison')}
            </TabsTrigger>
          )}
        </TabsList>
      </div>

      {/* Tender tab */}
      <TabsContent value="tender" className="mt-4 space-y-4">
        {activeJobId && activeJobContext === 'tender' && (
          <AiProcessingProgress jobId={activeJobId} onComplete={handleJobComplete} projectId={projectId} />
        )}
        <AiTenderTab
          tender={tender}
          jobs={jobs}
          refreshKey={refreshKey}
          onJobStarted={(jobId) => handleJobStarted(jobId, 'tender')}
          projectId={projectId}
        />
      </TabsContent>

      {/* Bid tabs — one per bidder */}
      {bids.map((bid: any) => {
        const processed = isBidProcessed(bid.id)
        const processing = isBidProcessing(bid.id)
        const tabId = `bid-${bid.id}`
        const bidJobId = activeJobContext === tabId ? activeJobId : getActiveBidJobId(bid.id)
        return (
          <TabsContent key={bid.id} value={tabId} className="mt-4 space-y-4">
            {bidJobId && (
              <AiProcessingProgress jobId={bidJobId} onComplete={handleJobComplete} projectId={projectId} />
            )}

            {/* Extract action bar */}
            <Card>
              <CardContent className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">{bid.bidder?.name || bid.id}</span>
                  {processed && !processing && (
                    <Badge variant="success" className="font-medium whitespace-nowrap">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {t('ai_completed')}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {processed && getBidJobId(bid.id) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      title={t('ai_processing_logs')}
                      onClick={() => setLogJobId(getBidJobId(bid.id)!)}
                    >
                      <ScrollText className="h-4 w-4" />
                    </Button>
                  )}
                  {canCreate && (
                    <Button
                      size="sm"
                      variant={processed ? 'outline' : 'default'}
                      onClick={() => handleStartBidExtraction(bid.id)}
                      disabled={extractingBid || processing}
                    >
                      {extractingBid || processing ? (
                        <LoaderCircle className="h-4 w-4 animate-spin" />
                      ) : processed ? (
                        <RotateCcw className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                      {processed ? t('ai_re_process') : t('ai_process_bid')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Extracted items — key forces remount with fresh data after reprocessing */}
            {processed && (
              <AiExtractedItemsTable
                key={`${bid.id}-${refreshKey}`}
                bidId={bid.id}
                projectId={projectId}
              />
            )}

            {!processed && !processing && (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <p className="text-sm">{t('ai_process_bids_desc')}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )
      })}

      {/* Comparison tab */}
      {allBidsProcessed && (
        <TabsContent value="comparison" className="mt-4 space-y-4">
          {activeJobId && activeJobContext === 'comparison' && (
            <AiProcessingProgress jobId={activeJobId} onComplete={handleJobComplete} projectId={projectId} />
          )}

          <Card>
            <CardContent className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3">
                <GitCompare className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('ai_compare_bids')}</span>
                {comparisonJob && (
                  <Badge variant="success" className="font-medium whitespace-nowrap">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {t('ai_completed')}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {canExport && <ExportEvaluationXLSX tender={tender} round={round} />}
                {bids.length > 1 && canCreate && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleProcessAllBids}
                    disabled={isBatchRunning}
                  >
                    {isBatchRunning ? (
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                    {t('ai_reprocess_all_bids', 'Reprocess All Bids')}
                  </Button>
                )}
                {canCreate && (
                  <Button
                    onClick={handleStartComparison}
                    disabled={comparing}
                    size="sm"
                  >
                    {comparing ? (
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                    ) : comparisonJob ? (
                      <RotateCcw className="h-4 w-4" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                    {comparisonJob ? t('ai_re_compare') : t('ai_start_comparison')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {comparisonJob && (
            <AiBidComparison key={`comparison-${refreshKey}`} tenderId={tender.id} round={round} />
          )}
        </TabsContent>
      )}
      {logJobId && (
        <AiProcessingLogModal
          jobId={logJobId}
          open={!!logJobId}
          onOpenChange={(open) => !open && setLogJobId(null)}
        />
      )}
    </Tabs>
  )
}

export default AiEvaluation
